import {
  AnalyticsEvent,
  ButtonComponentV2,
  Mutation,
  Typenames,
  UpdateExternalCardExpiryDateResult,
} from '@varo/graphql-schema-types';
import { useGlobalSearchParams, useRouter } from 'expo-router';
import {
  getFlowNameForAnalytics,
  logGenericErrorEvents,
  TabaPayEntryFlowType,
} from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import { useTabaPayAddress } from 'features/moveMoney/screens/dcf/tabapay/providers/TabaPayContextProvider';
import { getTabapayErrorScreen } from 'features/myVaro/screens/ExternalAccounts/helper/TabapayHelper';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UpdateExternalCardExpiryDate } from 'services/graphql/mutations/UpdateExternalCardExpiryDate';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { isCombinedError } from 'shared/utils/errors/combinedErrorUtils';
import { isCoreError } from 'shared/utils/errors/CoreError';
import { useMutation } from 'urql';

export enum SourceScreen {
  TRANSFER_SCREEN = 'transferScreen',
}

const useUpdateExternalCardExpiryDate = () => {
  const router = useRouter();
  const { tabaPayEntryFlowType } = useGlobalSearchParams<{
    tabaPayEntryFlowType: TabaPayEntryFlowType;
  }>();
  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'externalAccounts',
  });
  const [result, executeMutation] = useMutation<Mutation>(
    UpdateExternalCardExpiryDate,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [updateResult, setUpdateResult] =
    useState<UpdateExternalCardExpiryDateResult>();
  const [errorScreen, setErrorScreen] = useState<{
    screenType: ErrorScreenType.Error;
    messageHeadline: string;
    messageBody: string;
    primaryButton?: ButtonComponentV2;
    secondaryButton?: ButtonComponentV2;
    is3DSError?: boolean;
    analytics?: AnalyticsEvent;
  } | null>(null);
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();
  const { setUpdatedMessage } = useTabaPayAddress();
  const { segmentClient } = useAnalyticsConfiguration();

  const responseError = useFormattedCombinedError(result.error);

  useEffect(() => {
    if (responseError) {
      setErrorScreen({
        screenType: ErrorScreenType.Error,
        messageHeadline: responseError.title,
        messageBody: responseError.message,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.error]);

  const cardExpirationDateUpdatedEvent = usePressableAnalytics({
    analyticsId: 'CardExpirationDateUpdated',
    customAttributes: {
      flow_name: getFlowNameForAnalytics(tabaPayEntryFlowType),
    },
  });

  const updateExpiryDate = async (
    externalCardId: string,
    expirationDate: string,
    from?: string,
  ) => {
    try {
      setIsLoading(true);
      const result = await executeMutation({
        input: {
          externalCardId,
          expirationDate,
        },
      });
      const errorScreen = getTabapayErrorScreen(
        result?.data?.updateExternalCardExpiryDate,
      );
      if (errorScreen) {
        setErrorScreen(errorScreen);
      }
      if (
        result?.data?.updateExternalCardExpiryDate?.__typename ===
        Typenames.UpdateExternalCardExpiryDateResultError
      ) {
        logGenericErrorEvents(
          'Update External Card Expiry',
          'Generic_Error',
          segmentClient,
          errorScreen?.code,
          errorScreen?.messageBody,
        );
        setErrorScreen({
          screenType: ErrorScreenType.Error,
          messageHeadline: result.data.updateExternalCardExpiryDate.title,
          messageBody: result.data.updateExternalCardExpiryDate.message,
        });
      } else {
        setUpdateResult(result?.data?.updateExternalCardExpiryDate);
        if (
          result.data?.updateExternalCardExpiryDate?.__typename ===
            Typenames.UpdateExternalCardExpiryDateResultSuccess &&
          result.data.updateExternalCardExpiryDate.success
        ) {
          cardExpirationDateUpdatedEvent();
        }
        setUpdatedMessage(translatedText('snackbar.message'));
        if (from === SourceScreen.TRANSFER_SCREEN) {
          router.dismissTo(
            '/(authenticated)/move-money/tabapay/tabapay-transfer-screen',
          );
        } else {
          router.dismissTo({
            pathname: '/myvaro/external-accounts/[cardId]/card-details',
            params: {
              cardId: externalCardId,
            },
          });
        }
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      let errorTitle: string = defaultTitle;
      let errorMessage: string = defaultMessage;

      if (isCoreError(error)) {
        errorTitle = error.title;
        errorMessage = error.message;
      } else if (isCombinedError(error)) {
        errorMessage = error.message;
      }

      setErrorScreen({
        screenType: ErrorScreenType.Error,
        messageHeadline: errorTitle,
        messageBody: errorMessage,
      });
    }
  };
  return {
    updateExpiryDate,
    updateResult,
    isLoading,
    errorScreen,
  };
};

export default useUpdateExternalCardExpiryDate;
